import { TravellerWithCollectionMembershipsDTO } from '@/api/trafikportalen';
import { useListAllpayUsers } from '@/api/trafikportalen/trafikportalenAPI';
import { useListCardCredits } from '@/api/trafikportalen/trafikportalenAPI';
import { useMemo } from 'react';

export const useTravellerCreditInfo = (
  traveller: TravellerWithCollectionMembershipsDTO,
): {
  allpayCredit: number;
  cardCredit: number;
  totalTravellerCredit: number;
  isLoading: boolean;
} => {
  const { users: allpayUsers, isLoading: allpayIsLoading } =
    useListTravellerAllpayUsers(traveller);
  const { cards: cardCredits, isLoading: cardCreditsIsLoading } =
    useListTravellerCardCredits(traveller);

  if (allpayIsLoading || cardCreditsIsLoading)
    return {
      allpayCredit: 0,
      cardCredit: 0,
      totalTravellerCredit: 0,
      isLoading: true,
    };

  const allpayCredit = allpayUsers.reduce((acc, user) => acc + user.credit, 0);

  const cardCredit = cardCredits.reduce((acc, card) => acc + card.credit, 0);

  const totalTravellerCredit = allpayCredit + cardCredit;

  return {
    allpayCredit,
    cardCredit,
    totalTravellerCredit,
    isLoading: false,
  };
};

export const useListTravellerAllpayUsers = (
  traveller: TravellerWithCollectionMembershipsDTO,
) => {
  const { data: allpayUsers, isLoading: allpayIsLoading } =
    useListAllpayUsers();

  const users =
    useMemo(() => {
      if (!allpayUsers) return [];

      return allpayUsers.filter((user) =>
        traveller.memberships.some(
          (m) => m.collection_member_id === user.collection_member_id,
        ),
      );
    }, [allpayUsers, traveller.memberships]) ?? [];

  return {
    users,
    isLoading: allpayIsLoading,
  };
};

export const useListTravellerCardCredits = (
  traveller: TravellerWithCollectionMembershipsDTO,
) => {
  const { data: cardCredits, isLoading: cardCreditsIsLoading } =
    useListCardCredits();

  const cards =
    useMemo(() => {
      if (!cardCredits) return [];

      return cardCredits.filter((card) =>
        traveller.memberships.some(
          (membership) =>
            membership.collection_member_id === card.collection_member_id,
        ),
      );
    }, [cardCredits, traveller.memberships]) ?? [];

  return {
    cards,
    isLoading: cardCreditsIsLoading,
  };
};
